<!--
/**
 * APCWO customisation for OrangeHRM — Personal Review module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <div class="orangehrm-background-container apcwo-pr-root">
    <hr-nav active="cycles" />
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          Review Cycles
        </oxd-text>
        <oxd-button
          label="Add"
          icon-name="plus"
          display-type="secondary"
          @click="onClickAdd"
        />
      </div>
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :loading="isLoading"
          :headers="headers"
          :items="items?.data"
          :selectable="true"
          :clickable="false"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import HrNav from '../components/HrNav.vue';
import {API_BASE, formatDate} from '../util/helpers';

export default {
  name: 'CycleList',
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
    'hr-nav': HrNav,
  },
  setup() {
    const http = new APIService(window.appGlobal.baseUrl, `${API_BASE}/cycles`);
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http);
    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
    };
  },
  data() {
    return {
      headers: [
        {name: 'name', title: 'Cycle', style: {'flex-basis': '26%'}},
        {
          name: 'reviewYear',
          title: 'Year',
          style: {'flex-basis': '8%'},
        },
        {
          name: 'period',
          title: 'Period',
          style: {'flex-basis': '20%'},
          getValue: (item) =>
            `${formatDate(item.periodStart)} – ${formatDate(item.periodEnd)}`,
        },
        {
          name: 'dueDate',
          title: 'Due',
          style: {'flex-basis': '11%'},
          getValue: (item) => formatDate(item.dueDate),
        },
        {
          name: 'status',
          title: 'Status',
          style: {'flex-basis': '8%'},
          getValue: (item) => (item.status === 'open' ? 'Open' : 'Closed'),
        },
        {
          name: 'progress',
          title: 'Submitted / Staff',
          style: {'flex-basis': '15%'},
          getValue: (item) =>
            `${item.counts.submitted + item.counts.completed} / ${
              item.counts.total
            }`,
        },
        {
          name: 'actions',
          title: 'Actions',
          style: {'flex-basis': '12%'},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            delete: {
              onClick: this.onClickDelete,
              component: 'oxd-icon-button',
              props: {name: 'trash'},
            },
            view: {
              onClick: this.onClickReviews,
              component: 'oxd-icon-button',
              props: {name: 'eye-fill'},
            },
            edit: {
              onClick: this.onClickEdit,
              props: {name: 'pencil-fill'},
            },
          },
        },
      ],
      checkedItems: [],
    };
  },
  methods: {
    onClickAdd() {
      navigate('/personalReview/saveReviewCycle');
    },
    onClickEdit(item) {
      navigate('/personalReview/saveReviewCycle/{id}', {id: item.id});
    },
    onClickReviews(item) {
      navigate('/personalReview/viewPersonalReviews', {}, {cycleId: item.id});
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => this.items?.data[index].id);
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') this.deleteItems(ids);
      });
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') this.deleteItems([item.id]);
      });
    },
    deleteItems(ids) {
      this.isLoading = true;
      this.http
        .deleteAll({ids})
        .then(() => this.$toast.deleteSuccess())
        .catch(() => null)
        .then(() => {
          this.isLoading = false;
          this.checkedItems = [];
          return this.execQuery();
        });
    },
  },
};
</script>
